import { useState } from 'react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { resolveImage } from '@/lib/image';

export default function ProductCard({ product }) {
    const [selectedSize, setSelectedSize] = useState(null);
    const [isAdded, setIsAdded] = useState(false);
    const [activeVariant, setActiveVariant] = useState(
        product.color_variants && product.color_variants.length > 0
            ? product.color_variants[0]
            : null
    );
    const { addToCart } = useCart();

    const displayImage = resolveImage(activeVariant ? activeVariant.image_url : product.image_url);

    const handleAddToCart = () => {
        if (product.sizes.length > 0 && !selectedSize) {
            return;
        }
        const item = activeVariant
            ? { ...product, name: `${product.name} – ${activeVariant.color}`, image_url: activeVariant.image_url }
            : product;
        addToCart(item, selectedSize, 1);
        setIsAdded(true);
        setTimeout(() => setIsAdded(false), 2000);
    };

    return (
        <div
            data-testid={`product-card-${product.id}`}
            className="group bg-brand-surface border border-white/5 hover:border-white/20 transition-colors duration-500"
        >
            {/* Image */}
            <div className="relative aspect-square overflow-hidden bg-brand-highlight">
                <img
                    src={displayImage}
                    alt={product.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105"
                />
                {product.featured && (
                    <div
                        data-testid={`featured-pill-${product.id}`}
                        className="absolute top-3 left-3 z-10 px-3 py-1.5 bg-white text-black font-mono text-[10px] uppercase tracking-widest rounded-full shadow-lg"
                    >
                        <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full mr-2 animate-pulse" />
                        Featured Drop
                    </div>
                )}
                {!product.in_stock && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <span className="font-syne font-bold text-white uppercase tracking-widest">Sold Out</span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-6">
                <h3 className="font-syne font-bold text-white text-lg tracking-tight mb-2">
                    {product.name}
                </h3>
                <p className="font-space text-slate-400 text-sm mb-4 line-clamp-2">
                    {product.description}
                </p>
                <p className="font-syne font-bold text-white text-xl mb-6">
                    ${product.price.toFixed(2)}
                </p>

                {/* Color Variants */}
                {product.color_variants && product.color_variants.length > 0 && (
                    <div className="mb-6">
                        <p className="font-mono text-xs text-slate-500 uppercase tracking-widest mb-3">
                            Color: <span className="text-white">{activeVariant?.color}</span>
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {product.color_variants.map((variant) => (
                                <button
                                    key={variant.color}
                                    data-testid={`color-${variant.color}-${product.id}`}
                                    onClick={() => setActiveVariant(variant)}
                                    title={variant.color}
                                    aria-label={`Select ${variant.color}`}
                                    className={`w-8 h-8 rounded-full transition-all duration-300 ${
                                        activeVariant?.color === variant.color
                                            ? 'ring-2 ring-white ring-offset-2 ring-offset-brand-surface'
                                            : 'ring-1 ring-white/20 hover:ring-white/60'
                                    }`}
                                    style={{ backgroundColor: variant.hex }}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Size Selection */}
                {product.sizes.length > 0 && product.sizes[0] !== 'ONE SIZE' && (
                    <div className="mb-6">
                        <p className="font-mono text-xs text-slate-500 uppercase tracking-widest mb-3">Size</p>
                        <div className="flex flex-wrap gap-2">
                            {product.sizes.map((size) => (
                                <button
                                    key={size}
                                    data-testid={`size-${size}-${product.id}`}
                                    onClick={() => setSelectedSize(size)}
                                    className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-colors duration-300 ${
                                        selectedSize === size
                                            ? 'bg-white text-black border-white'
                                            : 'bg-transparent text-slate-400 border-brand-border hover:border-white/40'
                                    }`}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add to Cart Button */}
                <Button
                    data-testid={`add-to-cart-${product.id}`}
                    onClick={handleAddToCart}
                    disabled={!product.in_stock || (product.sizes.length > 0 && product.sizes[0] !== 'ONE SIZE' && !selectedSize)}
                    className={`w-full rounded-none py-4 font-bold uppercase tracking-widest transition-colors duration-300 ${
                        isAdded
                            ? 'bg-green-600 hover:bg-green-600 text-white'
                            : 'bg-white text-black hover:bg-slate-200'
                    }`}
                >
                    {!product.in_stock ? (
                        'SOLD OUT'
                    ) : isAdded ? (
                        <span className="flex items-center justify-center gap-2">
                            <Check className="w-4 h-4" /> ADDED
                        </span>
                    ) : (
                        'ADD TO CART'
                    )}
                </Button>
            </div>
        </div>
    );
}
